import { Alert, AlertTitle, Box, Button, DialogActions, DialogProps, DialogTitle, Divider } from '@mui/material';
import { PayorderParseReportDto } from '@sr/dto';
import FilePickerField from 'shared/ui/formik-fields/file-picker';
import { LegendItem, LegendList } from 'shared/ui/legend-list';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { Dialog } from 'shared/ui/dialog';
import { useState } from 'react';
import { useImport } from './uploadImportFile.hook';
import { Field, Form, Formik, FormikProps } from 'formik';
import { CreatePayOrderImportSessionDto, DownloadFileDto } from '@sr/dto';

type Props = {
  dialogProps: DialogProps,
  onUploadComplete: () => void;
};

export const NewImportDialog = ({ dialogProps, onUploadComplete }: Props) => {
  const [importReport, setImportReport] = useState<PayorderParseReportDto | null>(null);
  const [importError, setImportError] = useState('');
  const { uploadAndImport, loading: importInProgress } = useImport();


  const handleImport = async (data: {file: DownloadFileDto[]|null}) => {
    setImportError('');
    setImportReport(null);
    if (!data || !data['file'] || data['file'].length === 0) {
      setImportError('Не выбран файл для загрузки');
      return;
    }

    const payload: CreatePayOrderImportSessionDto = {
      importName: 'без названия',
      file: data['file'][0]
    };

    return uploadAndImport(payload)
      .then(res => {
        setImportReport(res);
        onUploadComplete();
      })
      .catch(e => setImportError(e.message));
  };


  const cleanUpAndClose = () => {
    setImportError('');
    setImportReport(null);
    dialogProps.onClose?.({}, 'escapeKeyDown');
  };

  return (
    <Dialog
      {...dialogProps}
      maxWidth='lg'
    >
      <DialogTitle>
        Новый импорт
        <Dialog.CloseButton onClick={cleanUpAndClose} />
      </DialogTitle>

      <Formik initialValues={{ file:null }} onSubmit={handleImport}>
        {({ submitForm, isSubmitting, values, touched, handleChange, errors }: FormikProps<any> ) => (
          <Form>
            <Dialog.Content>
              <Field name="file" label="Файл" component={FilePickerField} fileCategory={'pay-order-import'} pickerType={'doc'}/>
              <Divider />
              <Box>
                {importInProgress && <LoadingBanner />}
                {importReport && <ImportResult report={importReport} />}
                {importError && <ImportError error={importError} />}
              </Box>
            </Dialog.Content>
            <DialogActions>
              <Button
                variant="contained"
                type='submit'
                disabled={
                  !Object.keys(touched).length ||
                  isSubmitting ||
                  !!Object.keys(errors).length ||
                  importInProgress ||
                  importReport != null ||
                  importError.length !== 0
                }
              >
                Загрузить
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>

    </Dialog >);
};


export const useNewImportDialog = ({ onUploadComplete }: { onUploadComplete: () => void; }) => {
  const [open, setOpen] = useState(false);

  const openNewImportDialog = () => {
    setOpen(true);
  };

  const props: Props = {
    dialogProps: {
      open,
      maxWidth: 'md',
      onClose: (e: any, reason: string) => {
        if (reason !== 'backdropClick')
          setOpen(false);
      }
    },
    onUploadComplete: onUploadComplete
  };

  return {
    openNewImportDialog,
    props: props
  };
};

function ImportResult({ report }: { report: PayorderParseReportDto; }) {
  return (
    <>
      <Alert color='success'>
        <AlertTitle>Успешно</AlertTitle>
        <LegendList>
          <LegendItem title='Всего платежных поручений:' value={report.totalSectionsCount} />
          <LegendItem title='Успешно распознанных:' value={report.successfulParsedSectionsCount} />
          <LegendItem title='Неуспешно распознанных:' value={report.failedParsedSectionsCount} />
        </LegendList>
      </Alert>
    </>
  );
}

function ImportError({ error }: { error: string; }) {
  return (
    <>
      <Alert color='error'>
        <AlertTitle>Ошибка</AlertTitle>
        {error}
      </Alert>
    </>
  );
}
